import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

/**
 * Title21 Smart Support, through cgt-backend's /api/support (src/support).
 * The backend calls Smart Support for the X-Tenant-Id tenant only, and Smart
 * Support answers with the customer view: no internal notes, ever.
 */

export type SupportStatusValue =
  | 'new'
  | 'in_progress'
  | 'waiting_on_customer'
  | 'waiting_on_vendor'
  | 'scheduled'
  | 'escalated_engineering'
  | 'resolved'
  | 'closed'
export type SupportType = 'defect' | 'integration' | 'data_correction' | 'service_request' | 'question'
export type SupportPriority = 'P1' | 'P2' | 'P3' | 'P4'

/** GET /support/status: whether this tenant can reach Title21 Support. */
export interface SupportConnection {
  connected: boolean
  integrationName: string | null
  reason: 'not_configured' | 'tenant_not_mapped' | 'unreachable' | null
  customer?: string
  site?: { externalId: string; name: string; code: string } | null
  types?: SupportType[]
  priorities?: SupportPriority[]
  message?: string
}

export interface SupportSla {
  dueAt: string | null
  firstResponseDueAt: string | null
  firstRespondedAt: string | null
  clockRunning: boolean
  breached: boolean
}

/** A list item. `raisedByMe` comes from CGT, the rest from Smart Support. */
export interface SupportTicket {
  id: string
  number: number
  key: string
  subject: string
  type: SupportType
  priority: SupportPriority
  status: SupportStatusValue
  tenant: { id: string; name: string; code: string } | null
  assignee: { id: string; name: string } | null
  reporter: { id: string; name: string; email: string } | null
  sla: SupportSla | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  raisedByMe?: boolean
}

export interface SupportTicketDetail extends SupportTicket {
  context: { url?: string } | null
  slaPolicy: { name: string } | null
  reopenUntil: string | null
  closedAt: string | null
  resolution: { summary?: string } | null
  counts: { customerMessages: number; attachments: number }
}

export interface SupportAttachment {
  id: string
  fileName: string
  mimeType: string
  sizeBytes: number
  uploadedAt: string
}

export interface SupportMessage {
  id: string
  kind: string
  body: string
  author: { kind: 'customer' | 'agent' | 'system' | string; id: string | null; name: string; initials: string; subtitle?: string }
  createdAt: string
  edited: boolean
  attachments: SupportAttachment[]
}

export interface CreateSupportTicketInput {
  subject: string
  description: string
  type: SupportType
  priority?: SupportPriority
  pageUrl?: string
  reference?: string
}

export interface SupportTicketPage {
  items: SupportTicket[]
  nextCursor: string | null
}

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const headers = authHeaders({ 'X-Tenant-Id': tenantId, ...init?.headers }) as Record<string, string>
  // A FormData body needs the browser's own multipart Content-Type (with its
  // boundary), so the JSON default authHeaders adds must go.
  if (init?.body instanceof FormData) delete headers['Content-Type']
  const res = await authFetch(`${BASE}${path}`, { ...init, headers })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    throw new Error(message || `Title21 Support answered ${res.status}`)
  }
  return res.json() as Promise<T>
}

const enc = encodeURIComponent

export const supportApi = {
  status: (tenantId: string) => request<SupportConnection>('/support/status', tenantId),

  list: (tenantId: string, params: { status?: SupportStatusValue; cursor?: string } = {}) => {
    const qs = new URLSearchParams()
    if (params.status) qs.set('status', params.status)
    if (params.cursor) qs.set('cursor', params.cursor)
    const q = qs.toString()
    return request<SupportTicketPage>(`/support/tickets${q ? `?${q}` : ''}`, tenantId)
  },

  get: (tenantId: string, ticket: string | number) =>
    request<SupportTicketDetail>(`/support/tickets/${enc(String(ticket))}`, tenantId),

  thread: (tenantId: string, ticket: string | number) =>
    request<SupportMessage[]>(`/support/tickets/${enc(String(ticket))}/messages`, tenantId),

  create: (tenantId: string, input: CreateSupportTicketInput) =>
    request<SupportTicketDetail>('/support/tickets', tenantId, { method: 'POST', body: JSON.stringify(input) }),

  /** Returns Smart Support's post result; `message.id` is what files attach to. */
  reply: (tenantId: string, ticket: string | number, body: string) =>
    request<{ message: { id: string } }>(`/support/tickets/${enc(String(ticket))}/messages`, tenantId, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  attach: (tenantId: string, ticket: string | number, file: File, messageId?: string) => {
    const form = new FormData()
    form.append('file', file, file.name)
    if (messageId) form.append('messageId', messageId)
    return request<SupportAttachment & { messageId: string | null }>(`/support/tickets/${enc(String(ticket))}/attachments`, tenantId, {
      method: 'POST',
      body: form,
    })
  },

  /** A short-lived signed URL: a preview link (inline: true) when viewing a type that allows it, else a download. */
  attachmentUrl: (tenantId: string, ticket: string | number, attachmentId: string, view = false) =>
    request<{ url: string; fileName: string; mimeType: string; inline: boolean }>(
      `/support/tickets/${enc(String(ticket))}/attachments/${enc(attachmentId)}${view ? '?view=inline' : ''}`,
      tenantId,
    ),
}
